import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  GripVertical, 
  Calendar as CalendarIcon,
  Save,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GOVERNANCE_LEVELS = [
  { value: 'federal', label: 'Federal' },
  { value: 'state', label: 'State' },
  { value: 'lga', label: 'Local Government (LGA)' }
];

const POLICY_CATEGORIES = [
  { value: 'economy', label: 'Economy & Finance' },
  { value: 'security', label: 'Security & Safety' },
  { value: 'education', label: 'Education' },
  { value: 'health', label: 'Healthcare' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'youth', label: 'Youth Development' },
  { value: 'women', label: 'Women Affairs' },
  { value: 'jobs', label: 'Employment' }
];

const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'rating', label: 'Rating Scale (1-5)' },
  { value: 'open_ended', label: 'Open-Ended Text' }
];

export default function SurveyBuilder({ user }) {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const isEditing = !!surveyId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [survey, setSurvey] = useState({
    title: '',
    description: '',
    governance_level: 'federal',
    policy_category: 'economy',
    start_date: new Date(),
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    questions: []
  });

  useEffect(() => {
    if (isEditing) {
      fetchSurvey();
    }
  }, [surveyId]);

  const fetchSurvey = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/surveys/${surveyId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSurvey({
          ...data,
          start_date: new Date(data.start_date),
          end_date: new Date(data.end_date)
        });
      } else {
        toast.error('Failed to load survey');
        navigate('/admin');
      }
    } catch (error) {
      console.error('Error fetching survey:', error);
      toast.error('Failed to load survey');
    } finally {
      setLoading(false);
    }
  };

  const addQuestion = () => {
    const newQuestion = {
      question_id: `q_${Date.now()}`,
      text: '',
      question_type: 'multiple_choice',
      options: [
        { option_id: `opt_${Date.now()}_1`, text: '', order: 0 },
        { option_id: `opt_${Date.now()}_2`, text: '', order: 1 }
      ],
      required: true,
      order: survey.questions.length
    };
    setSurvey({ ...survey, questions: [...survey.questions, newQuestion] });
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...survey.questions];
    updated[index] = { ...updated[index], [field]: value };
    
    if (field === 'question_type') {
      if (value === 'multiple_choice') {
        updated[index].options = [
          { option_id: `opt_${Date.now()}_1`, text: '', order: 0 },
          { option_id: `opt_${Date.now()}_2`, text: '', order: 1 }
        ];
      } else {
        updated[index].options = [];
      }
    }
    
    setSurvey({ ...survey, questions: updated });
  };

  const removeQuestion = (index) => {
    const updated = survey.questions.filter((_, i) => i !== index);
    setSurvey({ ...survey, questions: updated });
  };

  const addOption = (questionIndex) => {
    const updated = [...survey.questions];
    const options = updated[questionIndex].options;
    options.push({
      option_id: `opt_${Date.now()}`,
      text: '',
      order: options.length
    });
    setSurvey({ ...survey, questions: updated });
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updated = [...survey.questions];
    updated[questionIndex].options[optionIndex].text = value;
    setSurvey({ ...survey, questions: updated });
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updated = [...survey.questions];
    updated[questionIndex].options = updated[questionIndex].options.filter((_, i) => i !== optionIndex);
    setSurvey({ ...survey, questions: updated });
  };

  const handleSubmit = async () => {
    if (!survey.title.trim()) {
      toast.error('Please enter a survey title');
      return;
    }
    if (survey.questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    for (const q of survey.questions) {
      if (!q.text.trim()) {
        toast.error('All questions must have text');
        return;
      }
      if (q.question_type === 'multiple_choice' && q.options.some(o => !o.text.trim())) {
        toast.error('All options must have text');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...survey,
        start_date: survey.start_date.toISOString(),
        end_date: survey.end_date.toISOString()
      };

      const url = isEditing ? `${API}/surveys/${surveyId}` : `${API}/surveys`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(isEditing ? 'Survey updated!' : 'Survey created!');
        navigate('/admin');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save survey');
      }
    } catch (error) {
      console.error('Error saving survey:', error);
      toast.error('Failed to save survey');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="survey-builder">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              </Link>
              <div className="h-6 w-px bg-border"></div>
              <h1 className="font-semibold">{isEditing ? 'Edit Survey' : 'Create Survey'}</h1>
            </div>
            <div className="flex items-center gap-2">
              {isEditing && (
                <Link to={`/survey/${surveyId}`} target="_blank">
                  <Button variant="outline" size="sm">
                    <Eye className="mr-2 h-4 w-4" />
                    Preview
                  </Button>
                </Link>
              )}
              <Button onClick={handleSubmit} disabled={saving} data-testid="save-survey-btn">
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Survey'}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Survey Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Survey Details</CardTitle>
                <CardDescription>Basic information about your survey</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={survey.title}
                    onChange={(e) => setSurvey({ ...survey, title: e.target.value })}
                    placeholder="e.g., 2024 Budget Priorities Survey"
                    data-testid="survey-title-input"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={survey.description}
                    onChange={(e) => setSurvey({ ...survey, description: e.target.value })}
                    placeholder="Describe the purpose of this survey..."
                    rows={3}
                    data-testid="survey-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Governance Level</Label>
                    <Select
                      value={survey.governance_level}
                      onValueChange={(value) => setSurvey({ ...survey, governance_level: value })}
                    >
                      <SelectTrigger data-testid="governance-level-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {GOVERNANCE_LEVELS.map((level) => (
                          <SelectItem key={level.value} value={level.value}>
                            {level.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Policy Category</Label>
                    <Select
                      value={survey.policy_category}
                      onValueChange={(value) => setSurvey({ ...survey, policy_category: value })}
                    >
                      <SelectTrigger data-testid="policy-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {POLICY_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Questions */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Questions</CardTitle>
                    <CardDescription>Add questions to your survey</CardDescription>
                  </div>
                  <Button onClick={addQuestion} size="sm" data-testid="add-question-btn">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Question
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {survey.questions.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed rounded-lg">
                    <p className="text-muted-foreground mb-4">No questions added yet</p>
                    <Button onClick={addQuestion} variant="outline">
                      <Plus className="mr-2 h-4 w-4" />
                      Add Your First Question
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {survey.questions.map((question, qIndex) => (
                      <div 
                        key={question.question_id} 
                        className="border rounded-lg p-4 bg-muted/30"
                        data-testid={`question-${qIndex}`}
                      >
                        <div className="flex items-start gap-3">
                          <GripVertical className="h-5 w-5 text-muted-foreground mt-2 cursor-grab" />
                          <div className="flex-1 space-y-4">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">Q{qIndex + 1}</Badge>
                              <Select
                                value={question.question_type}
                                onValueChange={(value) => updateQuestion(qIndex, 'question_type', value)}
                              >
                                <SelectTrigger className="w-48">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {QUESTION_TYPES.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <div className="flex-1"></div>
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`required-${qIndex}`} className="text-sm">Required</Label>
                                <Switch
                                  id={`required-${qIndex}`}
                                  checked={question.required}
                                  onCheckedChange={(checked) => updateQuestion(qIndex, 'required', checked)}
                                />
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => removeQuestion(qIndex)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                            
                            <Input
                              value={question.text}
                              onChange={(e) => updateQuestion(qIndex, 'text', e.target.value)}
                              placeholder="Enter your question..."
                              className="font-medium"
                            />

                            {question.question_type === 'multiple_choice' && (
                              <div className="space-y-2 pl-4">
                                {question.options.map((option, oIndex) => (
                                  <div key={option.option_id} className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded-full border-2 border-muted-foreground/30"></div>
                                    <Input
                                      value={option.text}
                                      onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                                      placeholder={`Option ${oIndex + 1}`}
                                      className="flex-1"
                                    />
                                    {question.options.length > 2 && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => removeOption(qIndex, oIndex)}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                ))}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => addOption(qIndex)}
                                  className="ml-6"
                                >
                                  <Plus className="mr-2 h-3 w-3" />
                                  Add Option
                                </Button>
                              </div>
                            )}

                            {question.question_type === 'rating' && (
                              <div className="flex items-center gap-2 pl-4">
                                {[1, 2, 3, 4, 5].map((num) => (
                                  <div 
                                    key={num} 
                                    className="w-10 h-10 rounded-lg border-2 border-muted-foreground/30 flex items-center justify-center text-sm font-medium"
                                  >
                                    {num}
                                  </div>
                                ))}
                              </div>
                            )}

                            {question.question_type === 'open_ended' && (
                              <div className="pl-4">
                                <Textarea 
                                  disabled 
                                  placeholder="Respondent's answer will appear here..."
                                  rows={2}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Schedule</CardTitle>
                <CardDescription>Set when the survey is active</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(survey.start_date, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={survey.start_date}
                        onSelect={(date) => date && setSurvey({ ...survey, start_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(survey.end_date, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={survey.end_date}
                        onSelect={(date) => date && setSurvey({ ...survey, end_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Questions</span>
                    <span className="font-medium">{survey.questions.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Level</span>
                    <Badge variant="outline">{survey.governance_level}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Category</span>
                    <Badge variant="secondary">{survey.policy_category}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
